<template>
  <footer v-if="showFooter" class="footer">
    <div class="footer-border"></div>
    
    <!-- Desktop navigation -->
    <div v-if="!isMobileView" class="footer-container">
      <!-- Left arrow for navigation -->
      <button 
        v-if="showArrows" 
        class="nav-arrow left-arrow" 
        :class="{ disabled: scrollPosition <= 0 }"
        @click="scrollLeft"
        aria-label="Scroll left"
      >
        <span class="mdi mdi-chevron-left"></span>
      </button>
      
      <!-- Scrollable nav container -->
      <nav ref="footerNav" class="footer-nav" @scroll="handleScroll">
        <template v-for="route in footerRoutes" :key="route.path">
          <router-link
            :to="route.meta.customRoute || route.path"
            class="nav-item"
            :class="{ active: isRouteActive(route) }"
            @click="playClickSound"
          >
            <div class="icon-container">
              <span :class="`mdi ${route.meta.icon}`"></span>
            </div>
            <span class="label">{{ getTranslatedLabel(route) }}</span>
          </router-link>
        </template>
      </nav>
      
      <!-- Right arrow for navigation -->
      <button 
        v-if="showArrows" 
        class="nav-arrow right-arrow" 
        :class="{ disabled: scrollPosition >= maxScrollPosition }"
        @click="scrollRight"
        aria-label="Scroll right"
      >
        <span class="mdi mdi-chevron-right"></span>
      </button>
    </div>
    
    <!-- Mobile navigation -->
    <div v-else class="mobile-footer">
      <!-- Mobile menu button -->
      <button 
        class="mobile-menu-button" 
        :class="{ active: mobileMenuOpen }"
        @click="toggleMobileMenu"
        aria-label="Toggle mobile menu"
      >
        <span class="mdi mdi-menu" v-if="!mobileMenuOpen"></span>
        <span class="mdi mdi-close" v-else></span>
        <span class="button-label">Menu</span>
      </button>
      
      <!-- Current page indicator (optional) -->
      <div class="current-page-indicator">
        <span v-if="currentRoute">{{ getTranslatedLabel(currentRoute) }}</span>
      </div>
    </div>
    
    <!-- Mobile slide-out menu -->
    <transition name="slide">
      <div v-if="mobileMenuOpen" class="mobile-slide-menu">
        <div class="mobile-menu-header">
          <h3>Navigation</h3>
          <button class="close-button" @click="toggleMobileMenu">
            <span class="mdi mdi-close"></span>
          </button>
        </div>
        
        <nav class="mobile-menu-nav">
          <template v-for="route in footerRoutes" :key="route.path">
            <router-link
              :to="route.meta.customRoute || route.path"
              class="mobile-nav-item"
              :class="{ active: isRouteActive(route) }"
              @click="navigateMobile(route)"
            >
              <div class="mobile-icon-container">
                <span :class="`mdi ${route.meta.icon}`"></span>
              </div>
              <span class="mobile-label">{{ getTranslatedLabel(route) }}</span>
            </router-link>
          </template>
        </nav>
      </div>
    </transition>
    
    <!-- Overlay for mobile menu -->
    <transition name="fade">
      <div 
        v-if="mobileMenuOpen" 
        class="mobile-menu-overlay"
        @click="toggleMobileMenu"
      ></div>
    </transition>
  </footer>
</template>

<script>
import { mapActions } from 'vuex'
import { useI18n } from 'vue-i18n'

export default {
  name: 'FooterComponent',
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      scrollPosition: 0,
      maxScrollPosition: 0,
      showArrows: false,
      navWidth: 0,
      containerWidth: 0,
      resizeObserver: null,
      isMobileView: false,
      mobileMenuOpen: false,
      mobileBreakpoint: 768 // Set mobile breakpoint
    }
  },
  computed: {
    showFooter() {
      return this.$route.meta.requiresAuth && this.$route.name !== 'CreateNewCompany'
    },
    footerRoutes() {
      return this.$router.options.routes.filter(route => route.meta?.showInFooter)
    },
    currentRoute() {
      // Find current active route to display in mobile view
      return this.footerRoutes.find(route => this.isRouteActive(route))
    }
  },
  watch: {
    // Close mobile menu on route change
    '$route'() {
      if (this.mobileMenuOpen) {
        this.mobileMenuOpen = false;
      }
    }
  },
  mounted() {
    this.checkViewportSize();
    this.checkOverflow();
    
    // Use ResizeObserver to detect changes in the footer nav's size
    this.resizeObserver = new ResizeObserver(() => {
      this.checkOverflow();
      this.checkViewportSize();
    });
    
    if (this.$refs.footerNav) {
      this.resizeObserver.observe(this.$refs.footerNav);
    }
    
    // Also listen for window resize events
    window.addEventListener('resize', this.handleResize);
    
    // Add body class when component is mounted
    document.body.classList.add('has-footer');
  },
  beforeUnmount() {
    // Clean up event listeners and observers
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.handleResize);
    
    // Remove body class when component is unmounted
    document.body.classList.remove('has-footer');
  },
  methods: {
    ...mapActions('SoundController', ['playSound']),
    getTranslatedLabel(route) {
      // Get the route name in lowercase to match the translation key
      const name = route.name.toLowerCase();
      
      // Try to get the translation for this route
      const translation = this.t(`footer.${name}`);
      
      // If translation exists and is not the same as the key, return it
      // Otherwise return the original label from the route metadata
      if (translation !== `footer.${name}`) {
        return translation;
      }
      
      // If no translation is found, return the label from route metadata
      return route.meta.label;
    },
    isRouteActive(route) {
      // Check if current route matches the main route or any of its children
      return this.$route.name && this.$route.name.startsWith(route.name)
    },
    playClickSound() {
      // Play UI click sound, same as in the header
      this.playSound({ category: 'ui', type: 'click' })
    },
    handleResize() {
      this.checkViewportSize();
      this.checkOverflow();
    },
    checkViewportSize() {
      // Check if we're in mobile view based on screen width
      this.isMobileView = window.innerWidth < this.mobileBreakpoint;
      
      // Close mobile menu when switching to desktop view
      if (!this.isMobileView && this.mobileMenuOpen) {
        this.mobileMenuOpen = false;
      }
    },
    checkOverflow() {
      if (!this.$refs.footerNav || this.isMobileView) return;
      
      const nav = this.$refs.footerNav;
      this.navWidth = nav.scrollWidth;
      this.containerWidth = nav.clientWidth;
      
      // Show arrows only if content is wider than container
      this.showArrows = this.navWidth > this.containerWidth;
      
      // Calculate max scroll position
      this.maxScrollPosition = this.navWidth - this.containerWidth;
      
      // Update scroll position in case the container got resized
      this.handleScroll();
    },
    handleScroll() {
      if (!this.$refs.footerNav) return;
      this.scrollPosition = this.$refs.footerNav.scrollLeft;
    },
    scrollLeft() {
      if (!this.$refs.footerNav) return;
      
      // Play UI click sound for navigation
      this.playClickSound();
      
      // Calculate scroll amount (one item width or fixed amount)
      const scrollAmount = Math.min(120, this.containerWidth * 0.3);
      
      // Smoothly scroll to the left
      this.$refs.footerNav.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    },
    scrollRight() {
      if (!this.$refs.footerNav) return;
      
      // Play UI click sound for navigation
      this.playClickSound();
      
      // Calculate scroll amount (one item width or fixed amount)
      const scrollAmount = Math.min(120, this.containerWidth * 0.3);
      
      // Smoothly scroll to the right
      this.$refs.footerNav.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    },
    toggleMobileMenu() {
      // Play UI click sound
      this.playClickSound();
      
      // Toggle mobile menu
      this.mobileMenuOpen = !this.mobileMenuOpen;
      
      // Add/remove class to body to prevent scrolling when menu is open
      if (this.mobileMenuOpen) {
        document.body.classList.add('mobile-menu-open');
      } else {
        document.body.classList.remove('mobile-menu-open');
      }
    },
    navigateMobile() {
      // Play UI click sound
      this.playClickSound();
      
      // Close mobile menu with slight delay to allow animation
      setTimeout(() => {
        this.mobileMenuOpen = false;
        document.body.classList.remove('mobile-menu-open');
      }, 150);
    }
  }
}
</script>

<style lang="scss" scoped>
.footer {
  position: fixed;
  bottom: 0;
  width: 100%;
  background: linear-gradient(to bottom, rgba(20, 20, 20, 0.95), rgba(30, 30, 30, 0.98));
  backdrop-filter: blur(10px);
  z-index: 100;
  padding: 0.5rem 0;
  box-shadow: 0 -4px 15px rgba(0, 0, 0, 0.5);
}

.footer-border {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, 
    transparent 0%, 
    rgba(255, 0, 0, 0.3) 20%, 
    rgba(255, 0, 0, 0.8) 50%,
    rgba(255, 0, 0, 0.3) 80%, 
    transparent 100%
  );
}

// Desktop footer styles
.footer-container {
  position: relative;
  display: flex;
  align-items: center;
  max-width: 900px;
  margin: 0 auto;
  padding: 0.5rem;
}

.footer-nav {
  display: flex;
  align-items: center;
  width: 100%;
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
  scroll-behavior: smooth;
  
  /* Hide scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Add padding for better visibility on scroll */
  padding: 0 0.5rem;
}

.nav-arrow {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  width: 32px;
  height: 32px;
  background: rgba(30, 30, 30, 0.8);
  border: 1px solid rgba(255, 51, 51, 0.3);
  border-radius: 50%;
  color: #ff3333;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  
  &:hover {
    background: rgba(255, 51, 51, 0.1);
    box-shadow: 0 0 15px rgba(255, 51, 51, 0.3);
  }
  
  &.disabled {
    opacity: 0.3;
    cursor: default;
    
    &:hover {
      background: rgba(30, 30, 30, 0.8);
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
    }
  }
  
  .mdi {
    font-size: 20px;
  }
}

.left-arrow {
  left: 0;
}

.right-arrow {
  right: 0;
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: #999;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
  position: relative;
  flex-shrink: 0; /* Prevent items from shrinking */
  
  &:hover, &.active {
    color: #ff3333;
    
    .icon-container {
      transform: translateY(-2px);
      background: rgba(255, 51, 51, 0.1);
      box-shadow: 0 0 15px rgba(255, 51, 51, 0.5);

      i {
        text-shadow: 0 0 10px rgba(255, 51, 51, 0.8);
      }
    }

    .label {
      text-shadow: 0 0 8px rgba(255, 51, 51, 0.5);
    }

    &::after {
      transform: scaleX(1);
      opacity: 1;
    }
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 25%;
    width: 50%;
    height: 2px;
    background: #ff3333;
    transform: scaleX(0);
    opacity: 0;
    transition: all 0.3s ease;
    box-shadow: 0 0 10px rgba(255, 51, 51, 0.5);
  }
}

.icon-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  margin-bottom: 0.3rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 51, 51, 0.2);

  .mdi {
    font-size: 24px;
  }
}

.label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  margin-top: 0.2rem;
  transition: all 0.3s ease;
}

// Mobile footer styles
.mobile-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
}

.mobile-menu-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(255, 51, 51, 0.1);
  border: 1px solid rgba(255, 51, 51, 0.3);
  border-radius: 8px;
  color: #ff3333;
  padding: 0.6rem 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &.active {
    background: rgba(255, 51, 51, 0.2);
    box-shadow: 0 0 15px rgba(255, 51, 51, 0.3);
  }
  
  .mdi {
    font-size: 24px;
    margin-bottom: 0.2rem;
  }
  
  .button-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
  }
  
  &:hover {
    background: rgba(255, 51, 51, 0.2);
    box-shadow: 0 0 15px rgba(255, 51, 51, 0.3);
  }
}

.current-page-indicator {
  color: #999;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

// Mobile Slide Menu
.mobile-slide-menu {
  position: fixed;
  top: -85vh;
  right: 0;
  height: 100vh;
  width: 80%;
  max-width: 320px;
  background: linear-gradient(to right, rgba(20, 20, 20, 0.98), rgba(30, 30, 30, 0.99));
  z-index: 200;
  box-shadow: -5px 0 20px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
}

.mobile-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 51, 51, 0.2);
  
  h3 {
    color: #ff3333;
    margin: 0;
    font-size: 1.2rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }
  
  .close-button {
    background: transparent;
    border: none;
    color: #999;
    font-size: 1.5rem;
    cursor: pointer;
    transition: color 0.3s ease;
    
    &:hover {
      color: #ff3333;
    }
  }
}

.mobile-menu-nav {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 0;
}

.mobile-nav-item {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  color: #999;
  text-decoration: none;
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
  
  &.active, &:hover {
    background: rgba(255, 51, 51, 0.1);
    color: #ff3333;
    border-left-color: #ff3333;
  }
  
  &.active {
    .mobile-icon-container {
      background: rgba(255, 51, 51, 0.1);
      box-shadow: 0 0 10px rgba(255, 51, 51, 0.3);
    }
  }
}

.mobile-icon-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  margin-right: 1rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 51, 51, 0.2);
  
  .mdi {
    font-size: 20px;
  }
}

.mobile-label {
  font-size: 0.9rem;
  font-weight: 500;
}

// Overlay for mobile menu
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  z-index: 150;
  backdrop-filter: blur(2px);
}

// Slide animation
.slide-enter-active, .slide-leave-active {
  transition: transform 0.3s ease-out;
}
.slide-enter-from, .slide-leave-to {
  transform: translateX(100%);
}
.slide-enter-to, .slide-leave-from {
  transform: translateX(0);
}

// Fade animation for overlay
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
.fade-enter-to, .fade-leave-from {
  opacity: 1;
}

// Utility classes for body
:global(body.mobile-menu-open) {
  overflow: hidden;
}
:global(body.has-footer) {
  padding-bottom: var(--footer-height, 75px);
}

// Responsive adjustments
@media (max-width: 640px) {
  .nav-item {
    padding: 0.5rem 0.7rem;
    
    .icon-container {
      width: 36px;
      height: 36px;
      
      .mdi {
        font-size: 22px;
      }
    }
    
    .label {
      font-size: 0.7rem;
    }
  }
}

@media (max-width: 480px) {
  .nav-item {
    padding: 0.5rem 0.5rem;
  }
  
  .footer-nav {
    justify-content: flex-start; /* Start alignment for better scrolling */
  }
}
</style>
